import { User } from '@firebase/auth';
import { collection, doc, DocumentReference, Timestamp, WriteBatch, writeBatch } from '@firebase/firestore';

import type {
  ActionedBy,
  IntakeStatusNote,
  CustomStep,
  CustomStepType,
  IntakeStatusBaseSteps,
  PatientIntakeStep,
  StatusActivityStatus,
  CurrentStatus,
  StatusActivity,
} from '@shared/patient-intake-status';
import type { UserType } from '@shared/auth/index.js';

import { db } from '../../../../firebase-config.js';
import type { UserData } from '../../../../types/UserData.temp.types.ts';
import { parseName } from '../utils';

type BaseUpdateStepParams = {
  batch: WriteBatch;
  status: StatusActivityStatus;
  actionedBy: NonNullable<CurrentStatus<Record<string, unknown> | null>['actionedBy']>;
  metadataOverride?: Record<string, unknown> | null;
} & (
  | {
      /**
       * The notes to be added to the step.
       */
      notes: string | null;
      isNoteVisibleToPatient: boolean;
      isMedicalNote: boolean;
    }
  | {
      /**
       * Using a previous note to attach to the status activity.
       */
      note: Pick<IntakeStatusNote, 'id' | 'visibility'> | null;
    }
);

type UpdateBaseStepParams<TStep extends IntakeStatusBaseSteps[keyof IntakeStatusBaseSteps]> = BaseUpdateStepParams & {
  patientRef: DocumentReference;
  step: TStep;
};

const updateBaseStep = <TStep extends IntakeStatusBaseSteps[keyof IntakeStatusBaseSteps]>({
  patientRef,
  status,
  actionedBy,
  step,
  batch,
  metadataOverride,
  ...params
}: UpdateBaseStepParams<TStep>) => {
  type BaseFieldStatusActivity = TStep['currentStatus'];

  const docRef = doc(patientRef, 'intakeStatus', step.id);
  const noteRef = doc(collection(docRef, 'intakeStatusNotes'));
  const intakeStatusActivityRef = doc(collection(patientRef, 'intakeStatusActivity'));

  const now = Timestamp.now();

  /**
   * Create a note if the notes are provided
   */
  if ('notes' in params && params.notes) {
    batch.set(noteRef, {
      content: params.notes,
      createdAt: now,
      createdById: actionedBy.id,
      visibility: {
        isVisibleToPatient: params.isNoteVisibleToPatient,
        isMedicalNote: params.isMedicalNote,
      },
    } satisfies Omit<IntakeStatusNote, 'id'>);
  }

  const resolvedMetadata =
    metadataOverride !== undefined
      ? metadataOverride
      : step.currentStatus.metadata
        ? { ...step.currentStatus.metadata }
        : null;

  const otherParams =
    status === 'COMPLETED' || status === 'PENDING'
      ? { status, metadata: resolvedMetadata }
      : { status, metadata: null };

  const note =
    'note' in params
      ? params.note
      : params.notes
        ? {
            id: noteRef.id,
            visibility: {
              isVisibleToPatient: params.isNoteVisibleToPatient,
              isMedicalNote: params.isMedicalNote,
            },
          }
        : null;

  const currentStatus = {
    note,
    actionedBy,
    actionedAt: now,
    ...otherParams,
  } satisfies BaseFieldStatusActivity;

  const baseStepUpdatePayload = {
    ...step,
    type: step.type,
    stepType: 'BASE_STEP',
    updatedAt: now,
    currentStatus,
  } satisfies Omit<TStep, 'id'>;

  batch.set(docRef, baseStepUpdatePayload, { merge: true });

  const actionPayload =
    step.currentStatus.status === status
      ? ({
          action: 'UPDATE_NOTE',
          metadata: {
            previousNote: step.currentStatus.note,
            newNote: note,
          },
        } satisfies Pick<StatusActivity<'UPDATE_NOTE'>, 'action' | 'metadata'>)
      : ({
          action: 'UPDATE_STATUS',
          metadata: {
            previousStatus: step.currentStatus.status,
            newStatus: status,
          },
        } satisfies Pick<StatusActivity<'UPDATE_STATUS'>, 'action' | 'metadata'>);

  const intakeStatusActivity = {
    ...actionPayload,
    actionedBy,
    stepId: step.id,
    actionedAt: now,
    stepSnapshot: { ...baseStepUpdatePayload } satisfies TStep,
  } satisfies StatusActivity<typeof actionPayload.action>;

  batch.set(intakeStatusActivityRef, intakeStatusActivity);
};

type UpdateCustomStepParams<TStep extends CustomStep[CustomStepType]> = BaseUpdateStepParams & {
  patientRef: DocumentReference;
  step: TStep;
};

const updateCustomStep = async <TStep extends CustomStep[CustomStepType]>({
  patientRef,
  batch,
  step,
  status,
  actionedBy,
  ...params
}: UpdateCustomStepParams<TStep>) => {
  const now = Timestamp.now();

  const docRef = doc(patientRef, 'intakeStatus', step.id);
  const noteRef = doc(collection(docRef, 'intakeStatusNotes'));
  const intakeStatusActivityRef = doc(collection(patientRef, 'intakeStatusActivity'));

  if ('notes' in params && params.notes) {
    batch.set(noteRef, {
      content: params.notes,
      createdAt: now,
      createdById: actionedBy.id,
      visibility: {
        isVisibleToPatient: params.isNoteVisibleToPatient,
        isMedicalNote: params.isMedicalNote,
      },
    } satisfies Omit<IntakeStatusNote, 'id'>);
  }

  const note =
    'note' in params
      ? params.note
      : params.notes
        ? {
            id: noteRef.id,
            visibility: {
              isVisibleToPatient: params.isNoteVisibleToPatient,
              isMedicalNote: params.isMedicalNote,
            },
          }
        : null;

  const otherParams =
    status === 'COMPLETED' || status === 'PENDING'
      ? { status, metadata: step.currentStatus?.metadata ? { ...step.currentStatus.metadata } : null }
      : { status, metadata: null };

  const currentStatus = {
    note,
    actionedBy,
    actionedAt: now,
    ...otherParams,
  } satisfies TStep['currentStatus'];

  const stepUpdatePayload = {
    currentStatus,
    updatedAt: now,
  } satisfies Pick<TStep, 'updatedAt' | 'currentStatus'>;

  batch.update(docRef, stepUpdatePayload);

  const actionPayload =
    step.currentStatus.status === status
      ? ({
          action: 'UPDATE_NOTE',
          metadata: {
            previousNote: step.currentStatus.note,
            newNote: note,
          },
        } satisfies Pick<StatusActivity<'UPDATE_NOTE'>, 'action' | 'metadata'>)
      : ({
          action: 'UPDATE_STATUS',
          metadata: {
            previousStatus: step.currentStatus.status,
            newStatus: status,
          },
        } satisfies Pick<StatusActivity<'UPDATE_STATUS'>, 'action' | 'metadata'>);

  batch.set(intakeStatusActivityRef, {
    ...actionPayload,
    actionedBy,
    actionedAt: now,
    stepId: step.id,
    healthCategoryId: step.healthCategoryId,
    stepSnapshot: { ...step, ...stepUpdatePayload } satisfies TStep,
  } satisfies StatusActivity<typeof actionPayload.action>);
};

type UpdateIntakeStatusParams = {
  user: User | null;
  userData: UserData | null;
  userType: UserType | '';
  status: StatusActivityStatus;
  step: PatientIntakeStep;
  patientId: string;
  metadataOverride?: Record<string, unknown> | null;
} & (
  | {
      notes: string | null;
      isNoteVisibleToPatient: boolean;
      isMedicalNote: boolean;
    }
  | {
      note: Pick<IntakeStatusNote, 'id' | 'visibility'> | null;
    }
);

export const updateIntakeStatus = async ({
  user,
  userData,
  userType,
  status,
  step,
  patientId,
  metadataOverride,
  ...params
}: UpdateIntakeStatusParams) => {
  if (!user || !userData || !userType) {
    throw new Error('Please login to update the status');
  }

  const actionedBy = {
    id: user.uid,
    name: parseName(userData.firstName, userData.lastName),
    email: user.email || 'no email',
    userType: userType,
  } satisfies ActionedBy;

  const patientRef = doc(collection(db, 'patients'), patientId);

  const batch = writeBatch(db);

  if (step.stepType === 'BASE_STEP') {
    updateBaseStep({
      batch,
      status,
      actionedBy,
      patientRef,
      step,
      metadataOverride,
      ...params,
    });

    await batch.commit();

    return;
  }

  if (step.stepType === 'CUSTOM_STEP' && step.id) {
    await updateCustomStep({
      batch,
      status,
      actionedBy,
      patientRef,
      step,
      ...params,
    });

    await batch.commit();

    return;
  }

  throw new Error('Invalid step type');
};
